import React, { useState, useEffect } from "react";
import appointmentService from "../services/appointmentService";
import "../styles/appointments.css";

const Toasts = ({ success, error }) => {
  if (!success && !error) return null;
  return (
    <div className="toast-container">
      {success && <div className="toast toast-success">{success}</div>}
      {error && <div className="toast toast-error">{error}</div>}
    </div>
  );
};

const TechLabAppointment = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    purpose: "",
    team_members: "",
    appointment_date: "",
    appointment_time: "",
    participant_count: 1,
    notes: ""
  });

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("form"); // form, list
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(""), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(""), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (activeTab === "list") {
      fetchAppointments();
    }
  }, [activeTab]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await appointmentService.getTechLabAppointments(null, token);
      setAppointments(response.appointments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === "participant_count" ? parseInt(value) : value
    }));
  };

  const isValidDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).getTime());

  const isValidTime = (value) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

  const isFutureAppointment = (dateValue, timeValue) => {
    if (!isValidDate(dateValue) || !isValidTime(timeValue)) return false;

    const appointmentDateTime = new Date(`${dateValue}T${timeValue}:00`);
    if (Number.isNaN(appointmentDateTime.getTime())) return false;

    return appointmentDateTime > new Date();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isValidDate(formData.appointment_date)) {
      setError("Geçerli bir tarih girin. Format: YYYY-MM-DD");
      return;
    }

    if (!isValidTime(formData.appointment_time)) {
      setError("Geçerli bir saat girin. Format: HH:MM");
      return;
    }

    if (!isFutureAppointment(formData.appointment_date, formData.appointment_time)) {
      setError("Geçmiş bir tarih veya saat için randevu oluşturulamaz.");
      return;
    }

    try {
      // team_members'ı array'e çevir
      const data = {
        ...formData,
        team_members: formData.team_members
          .split(",")
          .map(member => member.trim())
          .filter(member => member !== "")
      };

      setLoading(true);
      await appointmentService.createTechLabAppointment(data, token);
      
      setSuccess("Randevu başarıyla oluşturuldu!");
      setFormData({
        full_name: "",
        purpose: "",
        team_members: "",
        appointment_date: "",
        appointment_time: "",
        participant_count: 1,
        notes: ""
      });

      setTimeout(() => {
        setActiveTab("list");
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (appointmentId) => {
    if (window.confirm("Randevuyu iptal etmek istediğinize emin misiniz?")) {
      try {
        setLoading(true);
        await appointmentService.cancelTechLabAppointment(appointmentId, token);
        setSuccess("Randevu iptal edildi!");
        fetchAppointments();
        setTimeout(() => setSuccess(""), 2000);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="appointment-container">
      <div className="appointment-header">
        <h2>Necmettin Erbakan Teknoloji Atölyesi - Randevu Sistemi</h2>
        <p>Atölye kullanımı için randevu alabilirsiniz</p>
      </div>

      <div className="appointment-tabs">
        <button
          className={`tab-btn ${activeTab === "form" ? "active" : ""}`}
          onClick={() => setActiveTab("form")}
        >
          Yeni Randevu
        </button>
        <button
          className={`tab-btn ${activeTab === "list" ? "active" : ""}`}
          onClick={() => setActiveTab("list")}
        >
          Randevularım
        </button>
      </div>

      <Toasts success={success} error={error} />

      {activeTab === "form" && (
        <form className="appointment-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="full_name">Ad / Soyad *</label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              placeholder="Adınız ve soyadınız"
            />
          </div>

          <div className="form-group">
            <label htmlFor="purpose">Atölye Kullanım Amacı *</label>
            <textarea
              id="purpose"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              required
              placeholder="Atölyeyi ne amaçla kullanacağınızı açıklayınız"
              rows="4"
            />
          </div>

          <div className="form-group">
            <label htmlFor="team_members">Kullanacak Takım:</label>
            <input
              type="text"
              id="team_members"
              name="team_members"
              value={formData.team_members}
              onChange={handleChange}
              placeholder="Takım Adı veya Üyeler (virgülle ayrılmış)"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="appointment_date">Tarih *</label>
              <input
                type="date"
                id="appointment_date"
                name="appointment_date"
                value={formData.appointment_date}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="appointment_time">Saat *</label>
              <input
                type="time"
                id="appointment_time"
                name="appointment_time"
                value={formData.appointment_time}
                onChange={handleChange}
                min={formData.appointment_date === new Date().toISOString().split('T')[0] ? new Date().toTimeString().slice(0, 5) : undefined}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="participant_count">Kişi Sayısı *</label>
            <input
              type="number"
              id="participant_count"
              name="participant_count"
              value={formData.participant_count}
              onChange={handleChange}
              min="1"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Görüş ve Önerileriniz</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Ek talepleriniz varsa yazınız"
              rows="3"
            />
          </div>

          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? "Gönderiliyor..." : "Randevu Al"}
          </button>
        </form>
      )}

      {activeTab === "list" && (
        <div className="appointments-list">
          {loading ? (
            <p className="loading">Yükleniyor...</p>
          ) : appointments.length === 0 ? (
            <p className="empty-message">Henüz randevunuz yok</p>
          ) : (
            appointments.map(apt => (
              <div key={apt._id} className={`appointment-card status-${apt.status}`}>
                <div className="appointment-header-card">
                  <h3>{apt.full_name}</h3>
                  <span className={`status-badge ${apt.status}`}>{apt.status}</span>
                </div>
                <p><strong>Amaç:</strong> {apt.purpose}</p>
                {apt.team_members && apt.team_members.length > 0 && (
                  <p><strong>Takım:</strong> {apt.team_members.join(", ")}</p>
                )}
                <p><strong>Tarih & Saat:</strong> {apt.appointment_date} {apt.appointment_time}</p>
                <p><strong>Kişi Sayısı:</strong> {apt.participant_count}</p>
                {apt.notes && <p><strong>Notlar:</strong> {apt.notes}</p>}
                <p className="small-text">Oluşturulma: {new Date(apt.created_at).toLocaleString('tr-TR')}</p>
                
                {apt.status === "pending" && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleCancel(apt._id)}
                    disabled={loading}
                  >
                    İptal Et
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TechLabAppointment;
